import { hexToHSL } from './colorUtils';

/**
 * The typographic cover: a campaign with no photo shows its own name, set large in the
 * donor typeface on a brand tint (owner review, 2026-10-29).
 *
 * Long names take the first rule that fits, and a word is never cut at the frame:
 *   1. full  — the whole name, shrunk to a floor, on at most 3 lines (4 once small);
 *   2. keys  — filler words and years dropped;
 *   3. lead  — the leading key words that fit, at least two;
 *   4. mono  — initials.
 *
 * Geometry is in the SVG's own 1600×900 units, so the layout does not depend on the
 * size the cover is drawn at.
 */

export const COVER_WIDTH = 1600;
export const COVER_HEIGHT = 900;
export const COVER_FONT_WEIGHT = 700; // the heaviest Manrope face the donor apps load
export const TRACKING = -0.03; // letter-spacing, in em
export const WORD_GAP = 0.16; // extra word spacing, in em, so tight tracking never runs words together
export const LEADING = 0.92;

const PAD_X = 84;
const PAD_Y = 80;
const MAX_SIZE = 340;
const MIN_SIZE = 168;
const SIZE_STEP = 4;
const MONO_SIZE = 460;
const MIN_LEAD_WORDS = 2;
const MAX_INITIALS = 3;

const FILLER = new Set([
  'the', 'a', 'an', 'of', 'for', 'and', 'to', 'in', 'on', 'at', 'with', 'our', 'your', 'us',
  'help', 'support', 'annual', 'campaign', 'fund', 'fundraiser', 'appeal', '&', '-', '–', '·',
]);

const isKeyWord = (word) => !FILLER.has(word.toLowerCase()) && !/^\d+$/.test(word);

// Three lines while the type is large, four once it is small; never more.
const maxLinesAt = (size) => (size >= 210 ? 3 : 4);

/** Width of `text` at `size`, including the tracking and word gap the SVG applies. */
const trackedWidth = (measure, text, size) => {
  const gaps = (text.match(/ /g) || []).length;
  return measure(text, size) + TRACKING * size * Math.max(0, text.length - 1) + WORD_GAP * size * gaps;
};

function wrap(words, size, measure) {
  const maxWidth = COVER_WIDTH - PAD_X * 2;
  const lines = [];
  let line = '';
  for (const word of words) {
    if (trackedWidth(measure, word, size) > maxWidth) return null;
    const next = line ? `${line} ${word}` : word;
    if (trackedWidth(measure, next, size) <= maxWidth) {
      line = next;
    } else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/** The largest size whose wrapped lines fit both the width and the height of the frame. */
function fit(words, measure) {
  for (let size = MAX_SIZE; size >= MIN_SIZE; size -= SIZE_STEP) {
    const lines = wrap(words, size, measure);
    if (lines && lines.length <= maxLinesAt(size) && lines.length * size * LEADING <= COVER_HEIGHT - PAD_Y * 2) {
      return { size, lines };
    }
  }
  return null;
}

/**
 * Lay a campaign name out on the cover.
 * @param {string} title
 * @param {(text: string, size: number) => number} measure - untracked width of `text` at `size`
 * @returns {{ mode: 'full'|'keys'|'lead'|'mono', size: number, lines: string[] }}
 */
export function layoutCover(title, measure) {
  const words = String(title || '').trim().toUpperCase().split(/\s+/).filter(Boolean);
  if (!words.length) return null;

  const full = fit(words, measure);
  if (full) return { mode: 'full', ...full };

  const keys = words.filter(isKeyWord);
  if (keys.length && keys.length < words.length) {
    const fitted = fit(keys, measure);
    if (fitted) return { mode: 'keys', ...fitted };
  }

  // The leading key words carry the name's meaning ("Emergency Earthquake Relief").
  for (let n = keys.length - 1; n >= MIN_LEAD_WORDS; n -= 1) {
    const fitted = fit(keys.slice(0, n), measure);
    if (fitted) return { mode: 'lead', ...fitted };
  }

  const initials =
    words
      .filter((word) => !FILLER.has(word.toLowerCase()))
      .map((word) => word[0])
      .filter((char) => /\p{L}/u.test(char))
      .slice(0, MAX_INITIALS)
      .join('') || words[0][0];
  return { mode: 'mono', size: MONO_SIZE, lines: [initials] };
}

/** Baseline of each line, so the block sits centred in the frame. */
export function lineBaselines(layout) {
  const lineHeight = layout.size * LEADING;
  const top = (COVER_HEIGHT - layout.lines.length * lineHeight) / 2 + layout.size * 0.78;
  return layout.lines.map((_, i) => top + i * lineHeight);
}

export const coverTextX = (layout) => PAD_X - layout.size * 0.04;

/**
 * The cover's three tones from the nonprofit's colour: a pale tint behind, the colour
 * itself (held to a readable lightness) for the main lines, a lighter step for the
 * alternate lines. Saturation is clamped so a grey brand still reads as a colour and a
 * neon one does not vibrate.
 */
export function coverTones(brandHex) {
  const { h, s } = hexToHSL(brandHex);
  if (![h, s].every(Number.isFinite)) throw new Error('Invalid brand colour for the cover');
  const sat = Math.min(Math.max(s, 18), 70);
  // cs-allow-hardcoded: tones computed from the nonprofit's runtime colour, which no static token can hold
  const tone = (saturation, lightness) => `hsl(${Math.round(h)} ${saturation.toFixed(1)}% ${lightness}%)`;
  return { tint: tone(sat * 0.45, 91), mid: tone(sat * 0.8, 62), core: tone(sat, 40) };
}
